import { UserIdentityModel } from "client/Api/UserProfile/service";
import { FormattedText, Icon } from "client/Components";
import { useUserProfileStore } from "client/Features/Settings/UserProfile/userProfileStore";
import { localize } from "client_legacy/ndaw/culture";
import { ReactElement, useEffect, useState } from "react";
import styles from "./Greeting.module.less";
import { getStartOfDay, millisecondsInAnHour } from "client/DateAndTime/dateTimeUtils";
import { TimeZone } from "client/DateAndTime/timezones";
import { useUserTimezone } from "client/Hooks";

export const Greeting = (): ReactElement | null => {
	const userTimezone: TimeZone = useUserTimezone();

	const [localizationKeyWithExpiration, setLocalizationKeyWithExpiration] = useState<LocalizationKeyWithExpiration>(
		(): LocalizationKeyWithExpiration => getLocalizationKeyWithExpiration(new Date(), userTimezone)
	);

	const currentUserIdentity: UserIdentityModel | null = useUserProfileStore(
		x => x.currentUserIdentity
	);

	useEffect(
		() => {
			const timeoutHandle: SetTimeoutHandle = setTimeout(
				() => {
					setLocalizationKeyWithExpiration(
						getLocalizationKeyWithExpiration(new Date(), userTimezone)
					);
				},
				localizationKeyWithExpiration.expiresAfterMs
			);

			return () => clearTimeout(timeoutHandle);
		},
		[localizationKeyWithExpiration.expiresAfterMs, userTimezone]
	);

	if (!currentUserIdentity) {
		return (
			<span className={styles.root}>
				<span className={styles.line}>
					<span className={styles.text}>
						{localize(localizationKeyWithExpiration.localizationKey)}
					</span>
					<span className={styles.icon}>
						<Icon value={{kind: "svg", name: "wave"}} />
					</span>
				</span>
			</span>
		);
	}

	return (
		<span className={styles.root}>
			<FormattedText
				text={localize("_GreetingWithUserName")}
				thingsToReplace={[
					{ what: "{{Greeting}}", filler: localize(localizationKeyWithExpiration.localizationKey) },
					{
						what: "{{UserName}}",
						filler: (
							<span className={styles.line}>
								<span className={styles.text}>{currentUserIdentity.firstName}</span>
								<span className={styles.icon}>
									<Icon value={{kind: "svg", name: "wave"}} />
								</span>
							</span>
						)
					}
				]}
			/>
		</span>
	);
};
Greeting.displayName = "Greeting";

const fiveOClockMs: number = 5 * millisecondsInAnHour;
const twelveOClockMs: number = 12 * millisecondsInAnHour;
const eighteenOClockMs: number = 18 * millisecondsInAnHour;

type LocalizationKeyWithStartTime = {
	localizationKey: LocalizationKey;
	startTime: number;
};

const greetingTemplates: readonly LocalizationKeyWithStartTime [] = [
	{
		startTime: fiveOClockMs,
		localizationKey: "_GoodMorning"
	},
	{
		startTime: twelveOClockMs,
		localizationKey: "_GoodAfternoon"
	},
	{
		startTime: eighteenOClockMs,
		localizationKey: "_GoodEvening"
	}
];

type LocalizationKeyWithExpiration = {
	localizationKey: LocalizationKey;
	expiresAfterMs: number;
};

function getLocalizationKeyWithExpiration(
	date: Date,
	timezone: TimeZone
): LocalizationKeyWithExpiration {
	const startOfTheDay: Date = getStartOfDay(date, timezone);

	const msInTheDay: number = date.getTime() - startOfTheDay.getTime();

	if (greetingTemplates.length === 0) {
		throw new Error("No greetings are configured");
	}

	const firstGreeting: LocalizationKeyWithStartTime = greetingTemplates[0];
	const lastGreeting: LocalizationKeyWithStartTime = greetingTemplates[greetingTemplates.length - 1];
	let previousGreeting: LocalizationKeyWithStartTime = lastGreeting;

	for (const greeting of greetingTemplates) {
		if (greeting.startTime > msInTheDay) {
			return {
				localizationKey: previousGreeting.localizationKey,
				expiresAfterMs: greeting.startTime - msInTheDay
			};
		}

		previousGreeting = greeting;
	}

	return {
		localizationKey: lastGreeting.localizationKey,
		expiresAfterMs: firstGreeting.startTime + (24 * millisecondsInAnHour) - msInTheDay
	};
}